// @noflow
import { useCallback } from 'react'

import * as RibbonEvents from '@/constants/RibbonEvents'

type RibbonEventType = (typeof RibbonEvents)[keyof typeof RibbonEvents]

type TriggerWonderingEventArgs = {
  eventType: RibbonEventType
  options?: {
    condition: boolean
  }
}

type WonderingEvent = {
  triggerWonderingEvent: (args: TriggerWonderingEventArgs) => void
}

/**
 * # useWonderingEvent
 *
 * A hook for triggering Wondering events through the Ribbon integration.
 * Ribbon integration snippet can be found in the `app/views/shared/_ribbon.html.erb` file.
 *
 * @example
 * const { triggerWonderingEvent } = useWonderingEvent();
 *
 * // Trigger an event unconditionally
 * triggerWonderingEvent({ eventType: 'event_type_constant' });
 *
 * // Trigger an event only if a condition is met
 * triggerWonderingEvent({
 *   eventType: 'event_type_constant',
 *   options: { condition: newValue !== oldValue }
 * });
 *
 * @returns An object containing the triggerWonderingEvent function
 */
const useWonderingEvent = (): WonderingEvent => {
  /**
   * Triggers a Wondering event if conditions are met
   *
   * @param {RibbonEventType} eventType - The type of event to trigger
   * @param {boolean} [options.condition=true] - Whether the event should be triggered
   */
  const triggerWonderingEvent = useCallback(
    ({
      eventType,
      options = { condition: true }
    }: TriggerWonderingEventArgs): void => {
      if (options.condition && window.ribbon) {
        window.ribbon('trigger', eventType)
      }
    },
    []
  )

  return {
    triggerWonderingEvent
  }
}

export { useWonderingEvent }
